import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AnalyticsService, AssistantAccessService, AuthService, CashboxService, CommerceService, ToastService } from '@common/services';
import { EXTRA_USERS_SALES_ENABLED } from '@common/utils/extra-users.util';
import Intercom from '@intercom/messenger-js-sdk';
import { UserService } from '@modules/auth/services';
import { configSection } from '@modules/navigation/data/side-nav-dashboard.data';
import { SideNavItem, SideNavItems, SideNavSection } from '@modules/navigation/models';
import { NavigationService } from '@modules/navigation/services';
import { findCreditosItem, isCreditosItem } from '@modules/navigation/utils/creditos-nav-item.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { ReferralsComponent } from '../../components/referrals/referrals.component';

@Component({
    selector: 'sb-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['side-nav.component.scss'],
})
export class SideNavComponent implements OnInit, OnDestroy {
    @Input() sidenavStyle!: string;
    @Input() sideNavItems!: SideNavItems;
    @Input() sideNavSections!: SideNavSection[];
    configSection = configSection;

    /** FINA-3378 (canvas «Box copy»): Fina Presta sale de la lista de Módulos y va en el hueco sobre Configuración. */
    readonly isCreditosItem = isCreditosItem;

    get prestaItem(): SideNavItem | null {
        return findCreditosItem(this.sideNavSections, this.sideNavItems);
    }

    /** La línea del hueco se pinta solo si el ítem se ve: misma condición que `isItemHidden` del ítem. */
    showPresta(user: any): boolean {
        return !!this.prestaItem && !!user?.creditAccess;
    }

    public user$: Observable<any | null> = this.userService.user$.pipe(shareReplay({ bufferSize: 1, refCount: true }));

    /** ADM-790: con la venta de cupos apagada, crear usuarios vuelve a ser gratis (badge «Gratis»). */
    readonly extraUsersSalesEnabled = EXTRA_USERS_SALES_ENABLED;

    /**
     * Gate del CTA "Preguntar a Nina": dueño del comercio + plan pago. El botón solo se muestra
     * cuando las dos condiciones se cumplen. Se re-evalúa cuando llegan el usuario o el bundle de
     * planes, porque llegan async y sin eso el botón quedaría oculto para dueños válidos en el
     * primer pintado.
     */
    ninaEnabled: boolean = false;
    private flagsSubscription?: Subscription;

    constructor(
        public navigationService: NavigationService,
        public userService: UserService,
        public analyticsService: AnalyticsService,
        public cashboxService: CashboxService,
        public commerceService: CommerceService,
        private authService: AuthService,
        private toastService: ToastService,
        private modalService: NgbModal,
        private assistantAccess: AssistantAccessService,
    ) {}

    ngOnInit() {
        // Gate del CTA de Nina (dueño + plan). El stream ya emite el valor actual de arranque y
        // vuelve a emitir cuando llega cualquiera de las dos entradas.
        this.flagsSubscription = this.assistantAccess.canAccessChanges$().subscribe(enabled => {
            this.ninaEnabled = enabled;
        });
        this.commerceService._commerce$.pipe(take(2)).subscribe(commerce => {
            if (commerce?.cashBox?.enabled) {
                this.cashboxService.loadOpenCashbox().subscribe();
            }
        });
        this.userService.user$.pipe(take(1)).subscribe(user => {
            if (user && !this.authService.isSupportSession()) {
                const userHash = this.authService.getUserHash();
                const intercomConfig: any = {
                    app_id: environment.INTERCOM_APP_ID,
                    user_id: user._id,
                    name: `${user.name} ${user.lastName}`,
                    email: user.email,
                    phone: user.phone,
                    custom_attributes: {
                        whatsapp_number: user.phone,
                    },
                    company: {
                        company_id: user.tenantId,
                        name: user.tenantId,
                    },
                };

                if (userHash) {
                    intercomConfig.user_hash = userHash;
                }

                Intercom(intercomConfig);
            }
        });
    }

    /**
     * CTA de entrada a Nina. Nombre y props según el estándar de eventos AI
     * (`docs/ai-events-standard.md`): `<module>_<feature>_<accion>` más las 4 props base y el
     * `origen`, igual que `inventory_facturas_ia_cta_clicked`. Antes se emitía como
     * `sidenav_nina_button` sin ninguna prop, así que no se podía atribuir al módulo.
     */
    onNinaCtaClick(): void {
        const user: any = this.userService.user$.getValue();
        try {
            this.analyticsService.analyticsCapture('assistant_chat_cta_clicked', {
                tenant_id: user?.tenantId ?? null,
                user_id: user?._id ?? null,
                module: 'assistant',
                feature: 'chat',
                origen: 'sidenav',
            });
        } catch {
            /* no-op: la analítica nunca debe bloquear la navegación */
        }
    }

    ngOnDestroy() {
        this.flagsSubscription?.unsubscribe();
        this.modalService.dismissAll();
    }

    onToggleSwitch(event: Event) {
        if (!this.cashboxService.isCashboxOpen$.getValue() && !this.userService.user$.getValue().permissions.POS.includes('open-cashbox')) {
            event.preventDefault();
            this.toastService.error('Acceso denegado', 'No tienes acceso para abrir caja');
            return;
        }

        if (this.cashboxService.isCashboxOpen$.getValue() && !this.userService.user$.getValue().permissions.POS.includes('close-cashbox')) {
            event.preventDefault();
            this.toastService.error('Acceso denegado', 'No tienes acceso para cerrar caja');
            return;
        }

        this.cashboxService.isCashboxOpen = !this.cashboxService.isCashboxOpen$.getValue();
        if (this.cashboxService.isCashboxOpen$.getValue()) {
            this.cashboxService.openCashboxModal();
        }
        if (!this.cashboxService.isCashboxOpen$.getValue()) {
            this.cashboxService.closeCashboxModal();
        }
    }

    openReferralsModal() {
        this.analyticsService.analyticsCapture('sidenav_Referidos modal opened');
        const modalRef = this.modalService.open(ReferralsComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'referrals-modal-window',
        });

        // Cerrar el modal con la X o el fondo rechaza la promesa; no es un error.
        modalRef.result.catch(() => undefined);
    }

    // Escucha el botón atrás del navegador
    @HostListener('window:popstate', ['$event'])
    onPopState(event: any) {
        this.modalService.dismissAll();
    }
}
